"use client";

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

export const CC_SECTION_REVEAL_ROOT_CLASS = "cc-section-reveal";
export const CC_SECTION_REVEAL_VISIBLE_CLASS = "cc-section-reveal--visible";
export const CC_SECTION_REVEAL_STAGGER_CLASS = "cc-section-reveal--stagger-children";

const DEFAULT_THRESHOLD = 0.15;
const DEFAULT_ROOT_MARGIN = "0px 0px -10% 0px";
/** Any in-viewport pixel counts on mount (tall sections under half-heroes). */
const INITIAL_VISIBILITY_THRESHOLD = 0;

export type CcSectionRevealOptions = {
  threshold?: number;
  rootMargin?: string;
  /** When true, direct `.cc-container` children stagger in (see `_section-reveal.scss`). */
  staggerChildren?: boolean;
  /** When true, remove visible state when the section leaves the viewport (respects reduced motion). */
  reversible?: boolean;
};

export type CcSectionRevealRootMarginInsets = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

/** Parses CSS `rootMargin` (1–4 values, px or %) into pixel insets for the viewport root. */
export function parseCcSectionRevealRootMargin(
  rootMargin: string,
  viewportWidth: number,
  viewportHeight: number,
): CcSectionRevealRootMarginInsets {
  const parts = rootMargin.trim().split(/\s+/).filter(Boolean);
  const expanded =
    parts.length === 1
      ? [parts[0], parts[0], parts[0], parts[0]]
      : parts.length === 2
        ? [parts[0], parts[1], parts[0], parts[1]]
        : parts.length === 3
          ? [parts[0], parts[1], parts[2], parts[1]]
          : parts.slice(0, 4);

  const parseSide = (value: string, axis: "x" | "y") => {
    const trimmed = value.trim();
    if (trimmed.endsWith("%")) {
      const pct = Number.parseFloat(trimmed) / 100;
      const base = axis === "y" ? viewportHeight : viewportWidth;
      return Number.isFinite(pct) ? base * pct : 0;
    }
    const px = Number.parseFloat(trimmed);
    return Number.isFinite(px) ? px : 0;
  };

  return {
    top: parseSide(expanded[0] ?? "0px", "y"),
    right: parseSide(expanded[1] ?? "0px", "x"),
    bottom: parseSide(expanded[2] ?? "0px", "y"),
    left: parseSide(expanded[3] ?? "0px", "x"),
  };
}

type CcSectionRevealMeasureTarget = {
  getBoundingClientRect: () => DOMRectReadOnly;
};

/** Mirrors IntersectionObserver intersection ratio for the viewport root. */
export function getCcSectionRevealIntersectionRatio(
  element: CcSectionRevealMeasureTarget,
  {
    rootMargin = DEFAULT_ROOT_MARGIN,
    viewportWidth,
    viewportHeight,
  }: {
    rootMargin?: string;
    viewportWidth: number;
    viewportHeight: number;
  },
): number {
  if (viewportWidth <= 0 || viewportHeight <= 0) {
    return 0;
  }

  const insets = parseCcSectionRevealRootMargin(
    rootMargin,
    viewportWidth,
    viewportHeight,
  );
  // Signed margins match IntersectionObserver: negative bottom shrinks the root upward.
  const rootTop = -insets.top;
  const rootLeft = -insets.left;
  const rootBottom = viewportHeight + insets.bottom;
  const rootRight = viewportWidth + insets.right;

  const rect = element.getBoundingClientRect();
  const intersectionTop = Math.max(rect.top, rootTop);
  const intersectionLeft = Math.max(rect.left, rootLeft);
  const intersectionBottom = Math.min(rect.bottom, rootBottom);
  const intersectionRight = Math.min(rect.right, rootRight);

  if (
    intersectionBottom <= intersectionTop ||
    intersectionRight <= intersectionLeft
  ) {
    return 0;
  }

  const intersectionArea =
    (intersectionBottom - intersectionTop) *
    (intersectionRight - intersectionLeft);
  const targetArea = rect.width * rect.height;

  if (targetArea <= 0) {
    return 0;
  }

  return intersectionArea / targetArea;
}

export function isCcSectionRevealIntersecting(
  element: CcSectionRevealMeasureTarget,
  {
    threshold = DEFAULT_THRESHOLD,
    rootMargin = DEFAULT_ROOT_MARGIN,
    viewportWidth,
    viewportHeight,
  }: {
    threshold?: number;
    rootMargin?: string;
    viewportWidth: number;
    viewportHeight: number;
  },
): boolean {
  return (
    getCcSectionRevealIntersectionRatio(element, {
      rootMargin,
      viewportWidth,
      viewportHeight,
    }) >= threshold
  );
}

export function getCcSectionRevealClassName(
  isVisible: boolean,
  className?: string,
  { staggerChildren = false }: Pick<CcSectionRevealOptions, "staggerChildren"> = {},
) {
  return [
    CC_SECTION_REVEAL_ROOT_CLASS,
    staggerChildren ? CC_SECTION_REVEAL_STAGGER_CLASS : "",
    isVisible ? CC_SECTION_REVEAL_VISIBLE_CLASS : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * Section reveal via IntersectionObserver (latch-once by default; optional reversible).
 * Pass an existing ref when the section already observes itself for other behavior.
 */
export function useCcSectionReveal<T extends HTMLElement = HTMLElement>(
  options: CcSectionRevealOptions = {},
  externalRef?: RefObject<T | null>,
) {
  const {
    threshold = DEFAULT_THRESHOLD,
    rootMargin = DEFAULT_ROOT_MARGIN,
    staggerChildren = false,
    reversible = false,
  } = options;

  const prefersReducedMotion = useCcPrefersReducedMotion();
  const internalRef = useRef<T | null>(null);
  const ref = externalRef ?? internalRef;
  const [target, setTarget] = useState<T | null>(null);
  const [hasIntersected, setHasIntersected] = useState(false);
  const isVisible = prefersReducedMotion || hasIntersected;

  useLayoutEffect(() => {
    const node = ref.current;
    setTarget((current) => (current === node ? current : node));
  });

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    const node = target;
    if (!node) {
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      const id = window.requestAnimationFrame(() => {
        setHasIntersected(true);
      });
      return () => window.cancelAnimationFrame(id);
    }

    let latched = false;
    const rafIds: number[] = [];

    const measureViewport = () => ({
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    });

    const applyManualIntersection = () => {
      const { viewportWidth, viewportHeight } = measureViewport();
      const intersecting = isCcSectionRevealIntersecting(node, {
        threshold: INITIAL_VISIBILITY_THRESHOLD,
        rootMargin,
        viewportWidth,
        viewportHeight,
      });

      if (reversible) {
        setHasIntersected(intersecting);
        return;
      }

      if (intersecting && !latched) {
        latched = true;
        setHasIntersected(true);
        observer.disconnect();
      }
    };

    const applyEntry = (entry: IntersectionObserverEntry) => {
      if (reversible) {
        setHasIntersected(entry.isIntersecting);
        return;
      }

      if (!entry.isIntersecting || latched) {
        return;
      }

      latched = true;
      setHasIntersected(true);
      observer.disconnect();
    };

    const syncIntersection = () => {
      if (latched && !reversible) {
        return;
      }

      for (const entry of observer.takeRecords()) {
        applyEntry(entry);
        if (latched && !reversible) {
          return;
        }
      }

      applyManualIntersection();
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) {
          applyEntry(entry);
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(node);
    syncIntersection();

    rafIds.push(
      window.requestAnimationFrame(() => {
        syncIntersection();
        rafIds.push(window.requestAnimationFrame(syncIntersection));
      }),
    );

    const onLoad = () => syncIntersection();
    window.addEventListener("load", onLoad, { once: true });

    return () => {
      observer.disconnect();
      for (const id of rafIds) {
        window.cancelAnimationFrame(id);
      }
      window.removeEventListener("load", onLoad);
    };
  }, [prefersReducedMotion, reversible, rootMargin, target, threshold]);

  const withRevealClassName = (baseClassName?: string) =>
    getCcSectionRevealClassName(isVisible, baseClassName, { staggerChildren });

  return {
    ref,
    isVisible,
    withRevealClassName,
    staggerChildren,
  };
}
